import { ref, computed, onMounted, onUnmounted, type Ref } from 'vue';

// Quick-triage keyboard map (C3). Minimal by design — no 'S' snooze, no '/'
// search (both out of scope). Documented via title attributes on the affordances
// and the map below; there is no help modal.
//
//   J / ArrowDown  move focus to the next event card
//   K / ArrowUp    move focus to the previous event card
//   C              confirm the focused card
//   D              dismiss the focused card
//   Esc            clear the focus ring
//
// The handler is inert while any modal/popover is open (cellDetail, picker,
// inlinePhase, addTime, confirm dialog, regen dropdown) so keys typed into
// those surfaces never leak into triage, and inert while focus is in a text
// input so typing a note isn't hijacked.

export interface UseTriageKeyboardOptions {
  // Ordered list of focusable event ids (the inspector's attributed feed).
  eventIds: () => string[];
  // Any of these truthy → the keyboard is disabled entirely.
  blockers: Ref<boolean>[];
  onConfirm: (eventId: string) => void;
  onDismiss: (eventId: string) => void;
}

export function useTriageKeyboard(opts: UseTriageKeyboardOptions) {
  const focusedId = ref<string | null>(null);

  const disabled = computed(() => opts.blockers.some(b => b.value));

  function isTextEntry(el: EventTarget | null): boolean {
    if (!(el instanceof HTMLElement)) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }

  function scrollFocusedIntoView(): void {
    const id = focusedId.value;
    if (!id) return;
    // Runs after the class binding lands so the target exists in the DOM.
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-testid="event-row-${id}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  function move(delta: number): void {
    const ids = opts.eventIds();
    if (ids.length === 0) { focusedId.value = null; return; }
    const cur = focusedId.value ? ids.indexOf(focusedId.value) : -1;
    let next = cur + delta;
    if (cur === -1) next = delta > 0 ? 0 : ids.length - 1;
    next = Math.max(0, Math.min(ids.length - 1, next));
    focusedId.value = ids[next];
    scrollFocusedIntoView();
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (disabled.value || isTextEntry(e.target)) return;

    switch (e.key) {
      case 'j':
      case 'J':
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'k':
      case 'K':
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'c':
      case 'C':
        if (focusedId.value) { e.preventDefault(); opts.onConfirm(focusedId.value); }
        break;
      case 'd':
      case 'D':
        if (focusedId.value) { e.preventDefault(); opts.onDismiss(focusedId.value); }
        break;
      case 'Escape':
        if (focusedId.value) { e.preventDefault(); focusedId.value = null; }
        break;
    }
  }

  onMounted(() => document.addEventListener('keydown', onKeyDown));
  onUnmounted(() => document.removeEventListener('keydown', onKeyDown));

  return { focusedId, disabled };
}
